import { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState, type ClipboardEvent, type DragEvent, type KeyboardEvent } from 'react';
import { Icon, type IconName } from '../../components/Icon.tsx';
import { Button, Dialog } from '../../components/ui.tsx';
import { t, type MessageKey } from '../../i18n/index.ts';
import { action, upload } from '../../lib/api.ts';
import { shrinkPhoto } from '../../lib/images.ts';
import { useUi } from '../../lib/ui-state.tsx';
import { Inline, plain, storedOffset, toggleMark } from './inline.tsx';

export const BLOCK_TYPES = ['p', 'h1', 'h2', 'h3', 'bullet', 'number', 'todo', 'quote', 'callout', 'code', 'divider', 'image'] as const;
export type BlockType = (typeof BLOCK_TYPES)[number];
export const CALLOUT_TONES = ['info', 'success', 'warning', 'danger'] as const;
export type CalloutTone = (typeof CALLOUT_TONES)[number];
export interface Block {
  id: string;
  type: BlockType;
  text: string;
  checked?: boolean;
  indent?: number;
  tone?: CalloutTone;
  image?: string;
}
export interface PageRef {
  id: string;
  title: string;
  icon: string | null;
}

const LISTS: BlockType[] = ['bullet', 'number', 'todo'];
const TEXTLESS: BlockType[] = ['divider'];
const TONE_ICON: Record<CalloutTone, IconName> = { info: 'info', success: 'check', warning: 'alert', danger: 'alert' };
export const newId = () => Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);
const blank = (type: BlockType = 'p', extra: Partial<Block> = {}): Block => ({ id: newId(), type, text: '', ...extra });

/** What a block of each type is called in menus. */
export const TYPE_LABEL: Record<BlockType, MessageKey> = {
  p: 'kb.t_p',
  h1: 'kb.t_h1',
  h2: 'kb.t_h2',
  h3: 'kb.t_h3',
  bullet: 'kb.t_bullet',
  number: 'kb.t_number',
  todo: 'kb.t_todo',
  quote: 'kb.t_quote',
  callout: 'kb.t_callout',
  code: 'kb.t_code',
  divider: 'kb.t_divider',
  image: 'kb.t_image',
};
const TYPE_ICON: Record<BlockType, IconName> = {
  p: 'text',
  h1: 'heading',
  h2: 'heading',
  h3: 'heading',
  bullet: 'list',
  number: 'listOrdered',
  todo: 'checkSquare',
  quote: 'quote',
  callout: 'info',
  code: 'code',
  divider: 'minus',
  image: 'image',
};
const TYPE_HINT: Partial<Record<BlockType, string>> = { h1: '#', h2: '##', h3: '###', bullet: '-', number: '1.', todo: '[]', quote: '>', code: '```', divider: '---' };

type SlashItem = { key: BlockType | 'subpage' | 'pagelink'; label: string; icon: IconName; hint?: string; words: string };
const slashItems = (): SlashItem[] => [
  ...BLOCK_TYPES.map((k) => ({ key: k, label: t(TYPE_LABEL[k]), icon: TYPE_ICON[k], hint: TYPE_HINT[k], words: `${t(TYPE_LABEL[k])} ${k}`.toLowerCase() })),
  { key: 'subpage' as const, label: t('kb.slash_subpage'), icon: 'notebook' as IconName, words: `${t('kb.slash_subpage')} page`.toLowerCase() },
  { key: 'pagelink' as const, label: t('kb.slash_pagelink'), icon: 'link' as IconName, words: `${t('kb.slash_pagelink')} link`.toLowerCase() },
];

/** "# Title", "- item", "[ ] to do" and the like, as blocks: for pasting and for Markdown. */
export function fromLines(lines: string[]): Block[] {
  const out: Block[] = [];
  let code: Block | null = null;
  for (const raw of lines) {
    if (code) {
      if (raw.trim().startsWith('```')) {
        out.push(code);
        code = null;
      } else code.text = code.text ? `${code.text}\n${raw}` : raw;
      continue;
    }
    const indent = Math.min(4, Math.floor((raw.match(/^ */)?.[0].length ?? 0) / 2));
    const l = raw.trim();
    if (l.startsWith('```')) {
      code = blank('code');
      continue;
    }
    let m: RegExpMatchArray | null;
    if (!l) continue;
    if (/^(-{3,}|\*{3,})$/.test(l)) out.push(blank('divider'));
    else if ((m = l.match(/^(#{1,3})\s+(.*)$/))) out.push(blank(`h${m[1]!.length}` as BlockType, { text: m[2]! }));
    else if ((m = l.match(/^[-*+]\s+\[([ xX])\]\s*(.*)$/)) || (m = l.match(/^\[([ xX]?)\]\s*(.*)$/)))
      out.push(blank('todo', { text: m[2]!, checked: /x/i.test(m[1]!), ...(indent ? { indent } : {}) }));
    else if ((m = l.match(/^[-*+•]\s+(.*)$/))) out.push(blank('bullet', { text: m[1]!, ...(indent ? { indent } : {}) }));
    else if ((m = l.match(/^\d+[.)]\s+(.*)$/))) out.push(blank('number', { text: m[1]!, ...(indent ? { indent } : {}) }));
    else if ((m = l.match(/^>\s?(.*)$/))) out.push(blank('quote', { text: m[1]! }));
    else out.push(blank('p', { text: l }));
  }
  if (code) out.push(code);
  return out;
}

/** The page as Markdown, to download. */
export function toMarkdown(title: string, blocks: Block[]): string {
  const lines: string[] = title ? [`# ${title}`, ''] : [];
  let n = 0;
  for (const b of blocks) {
    const pad = '  '.repeat(b.indent ?? 0);
    n = b.type === 'number' ? n + 1 : 0;
    const line =
      b.type === 'h1'
        ? `## ${b.text}`
        : b.type === 'h2'
          ? `### ${b.text}`
          : b.type === 'h3'
            ? `#### ${b.text}`
            : b.type === 'bullet'
              ? `${pad}- ${b.text}`
              : b.type === 'number'
                ? `${pad}${n}. ${b.text}`
                : b.type === 'todo'
                  ? `${pad}- [${b.checked ? 'x' : ' '}] ${b.text}`
                  : b.type === 'quote'
                    ? `> ${b.text}`
                    : b.type === 'callout'
                      ? `> **${t(`kb.tone_${b.tone ?? 'info'}` as MessageKey)}:** ${b.text}`
                      : b.type === 'code'
                        ? `\`\`\`\n${b.text}\n\`\`\``
                        : b.type === 'divider'
                          ? '---'
                          : b.type === 'image'
                            ? `*[${t('kb.t_image')}${b.text ? `: ${b.text}` : ''}]*`
                            : b.text;
    lines.push(line, '');
  }
  return lines.join('\n');
}

// ---- pictures: fetched with the sign-in, kept for the session ------------------------------------

const pictures = new Map<string, Promise<string>>();
export function pictureUrl(id: string) {
  let p = pictures.get(id);
  if (!p) {
    p = action<{ mime: string; data: string }>('knowledge.image', { id }).then(({ mime, data }) => {
      const bin = atob(data);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      return URL.createObjectURL(new Blob([bytes], { type: mime }));
    });
    p.catch(() => pictures.delete(id));
    pictures.set(id, p);
  }
  return p;
}
function Picture({ id, alt }: { id: string; alt: string }) {
  const [src, setSrc] = useState<string | null>(null);
  const [broken, setBroken] = useState(false);
  useEffect(() => {
    let live = true;
    pictureUrl(id).then(
      (u) => live && setSrc(u),
      () => live && setBroken(true),
    );
    return () => {
      live = false;
    };
  }, [id]);
  if (broken)
    return (
      <span className="kb-pic-missing">
        <Icon name="image" /> {t('kb.picture_missing')}
      </span>
    );
  return src ? <img src={src} alt={alt} /> : <span className="kb-pic-loading skeleton" aria-hidden="true" />;
}

/** Grows with its text, like the paragraph it stands in for. */
function AutoText({ value, onRef, ...rest }: React.TextareaHTMLAttributes<HTMLTextAreaElement> & { value: string; onRef: (el: HTMLTextAreaElement | null) => void }) {
  const el = useRef<HTMLTextAreaElement | null>(null);
  useLayoutEffect(() => {
    const a = el.current;
    if (!a) return;
    a.style.height = '0px';
    a.style.height = `${a.scrollHeight}px`;
  }, [value]);
  return (
    <textarea
      {...rest}
      value={value}
      rows={1}
      spellCheck
      ref={(x) => {
        el.current = x;
        onRef(x);
      }}
    />
  );
}

export interface EditorHandle {
  /** Puts the cursor in the first block, making one when the page is empty. */
  focusStart: () => void;
}

/**
 * The page's blocks, written like a document: Enter starts a new block, Backspace at the start
 * joins it to the one above, "/" opens the list of block kinds, Markdown shortcuts ("# ", "- ",
 * "[] ", "> ", "```", "---") change the kind as they are typed, Tab indents a list.
 */
export const Editor = forwardRef<
  EditorHandle,
  {
    pageId: string;
    blocks: Block[];
    onChange: (blocks: Block[]) => void;
    readOnly: boolean;
    pages: PageRef[];
    onSubpage: () => Promise<PageRef | null>;
    onTitleFocus: () => void;
  }
>(function Editor({ pageId, blocks, onChange, readOnly, pages, onSubpage, onTitleFocus }, ref) {
  const { fail } = useUi();
  const [active, setActive] = useState<string | null>(null);
  const [slash, setSlash] = useState<{ id: string; query: string; index: number } | null>(null);
  const [menu, setMenu] = useState<string | null>(null);
  const [selBar, setSelBar] = useState<string | null>(null);
  const [linking, setLinking] = useState<{ id: string; start: number; end: number; replace?: boolean } | null>(null);
  const [drag, setDrag] = useState<{ id: string; over: string | null; after: boolean } | null>(null);
  const [uploading, setUploading] = useState(0);
  const areas = useRef(new Map<string, HTMLTextAreaElement>());
  const caret = useRef<{ id: string; at: number; end?: number } | null>(null);
  const current = useRef(blocks);
  current.current = blocks;
  const fileFor = useRef<{ after: string | null; replace: string | null }>({ after: null, replace: null });
  const file = useRef<HTMLInputElement>(null);

  const set = (next: Block[]) => {
    current.current = next;
    onChange(next);
  };
  const focus = (id: string, at: number | 'end' = 'end', end?: number) => {
    const b = current.current.find((x) => x.id === id);
    if (!b) return;
    const pos = at === 'end' ? b.text.length : at;
    caret.current = { id, at: pos, end: end ?? pos };
    setActive(id);
  };
  useLayoutEffect(() => {
    const c = caret.current;
    if (!c) return;
    const a = areas.current.get(c.id);
    if (a) {
      a.focus({ preventScroll: false });
      a.setSelectionRange(Math.min(c.at, a.value.length), Math.min(c.end ?? c.at, a.value.length));
      caret.current = null;
    } else if (TEXTLESS.includes(current.current.find((b) => b.id === c.id)?.type ?? 'p')) {
      document.querySelector<HTMLElement>(`[data-block="${c.id}"] .kb-divider`)?.focus();
      caret.current = null;
    }
  });
  useImperativeHandle(ref, () => ({
    focusStart() {
      if (readOnly) return;
      const first = current.current[0];
      if (first && !TEXTLESS.includes(first.type) && first.type !== 'image') return focus(first.id, 0);
      const b = blank();
      set([b, ...current.current]);
      focus(b.id, 0);
    },
  }));

  const patch = (id: string, p: Partial<Block>) => set(current.current.map((b) => (b.id === id ? { ...b, ...p } : b)));
  const index = (id: string) => current.current.findIndex((b) => b.id === id);
  const insertAfter = (id: string | null, add: Block[]) => {
    const list = [...current.current];
    const i = id ? list.findIndex((b) => b.id === id) : list.length - 1;
    list.splice(i + 1, 0, ...add);
    set(list);
  };
  const editable = (b: Block | undefined) => !!b && !TEXTLESS.includes(b.type);
  const prevEditable = (i: number) => {
    for (let j = i - 1; j >= 0; j--) if (editable(current.current[j])) return current.current[j]!;
    return null;
  };
  const nextEditable = (i: number) => {
    for (let j = i + 1; j < current.current.length; j++) if (editable(current.current[j])) return current.current[j]!;
    return null;
  };
  const turnInto = (id: string, type: BlockType) => {
    const b = current.current.find((x) => x.id === id);
    if (!b) return;
    if (type === 'image') {
      fileFor.current = { after: null, replace: id };
      file.current?.click();
      return;
    }
    if (type === 'divider') {
      const nb = blank();
      set(current.current.flatMap((x) => (x.id === id ? [{ id: x.id, type: 'divider' as const, text: '' }, ...(index(id) === current.current.length - 1 ? [nb] : [])] : [x])));
      const after = current.current[index(id) + 1];
      if (after) focus(after.id, 0);
      return;
    }
    const next: Block = { id: b.id, type, text: b.type === 'divider' ? '' : b.text };
    if (type === 'todo') next.checked = false;
    if (type === 'callout') next.tone = b.tone ?? 'info';
    if (LISTS.includes(type) && LISTS.includes(b.type) && b.indent) next.indent = b.indent;
    if (b.type === 'image') next.text = b.text;
    set(current.current.map((x) => (x.id === id ? next : x)));
    focus(id);
  };

  const remove = (id: string) => {
    const i = index(id);
    const prev = prevEditable(i) ?? nextEditable(i);
    set(current.current.filter((b) => b.id !== id));
    if (prev) focus(prev.id);
  };
  const moveBy = (id: string, d: number) => {
    const list = [...current.current];
    const i = list.findIndex((b) => b.id === id);
    const j = i + d;
    if (j < 0 || j >= list.length) return;
    [list[i], list[j]] = [list[j]!, list[i]!];
    set(list);
  };
  const moveTo = (id: string, target: string, after: boolean) => {
    if (id === target) return;
    const list = current.current.filter((b) => b.id !== id);
    const moving = current.current.find((b) => b.id === id)!;
    const k = list.findIndex((b) => b.id === target);
    list.splice(after ? k + 1 : k, 0, moving);
    set(list);
  };

  // ---- pictures --------------------------------------------------------------------------------
  const addPictures = async (files: File[], after: string | null, replace: string | null = null) => {
    const list = files.filter((f) => f.type.startsWith('image/'));
    if (!list.length) return;
    setUploading((n) => n + list.length);
    let anchor = after;
    for (const f of list) {
      try {
        const { base64 } = await shrinkPhoto(f, 1600, 0.85);
        const { id } = await upload<{ id: string }>('knowledge.upload_image', { id: pageId, name: f.name || 'picture.jpg', data: base64 });
        const b: Block = { id: replace ?? newId(), type: 'image', text: '', image: id };
        if (replace) {
          set(current.current.map((x) => (x.id === replace ? b : x)));
          replace = null;
        } else insertAfter(anchor, [b]);
        anchor = b.id;
      } catch (e) {
        fail(e instanceof Error && e.message === 'not_image' ? new Error(t('kb.not_picture')) : e);
      } finally {
        setUploading((n) => n - 1);
      }
    }
    if (anchor && !current.current[index(anchor) + 1]) insertAfter(anchor, [blank()]);
  };

  // ---- slash menu ------------------------------------------------------------------------------
  const items = slash ? slashItems().filter((x) => !slash.query || x.words.includes(slash.query.toLowerCase())) : [];
  const applySlash = async (it: SlashItem) => {
    const id = slash!.id;
    setSlash(null);
    patch(id, { text: '' });
    if (it.key === 'subpage') {
      const page = await onSubpage();
      if (page) patch(id, { type: 'p', text: `[${page.title || t('kb.untitled')}](/knowledge/${page.id})` });
      return;
    }
    if (it.key === 'pagelink') {
      setLinking({ id, start: 0, end: 0, replace: true });
      return;
    }
    turnInto(id, it.key);
  };

  // ---- typing ----------------------------------------------------------------------------------
  const onText = (b: Block, value: string, el: HTMLTextAreaElement) => {
    // Markdown shortcuts at the start of a plain paragraph.
    if (b.type === 'p' || b.type === 'bullet' || b.type === 'number') {
      const rules: [RegExp, BlockType, Partial<Block>?][] = [
        [/^#\s$/, 'h1'],
        [/^##\s$/, 'h2'],
        [/^###\s$/, 'h3'],
        [/^[-*+]\s$/, 'bullet'],
        [/^1[.)]\s$/, 'number'],
        [/^\[\s?\]\s$/, 'todo'],
        [/^\[x\]\s$/i, 'todo', { checked: true }],
        [/^>\s$/, 'quote'],
        [/^```$/, 'code'],
        [/^---$/, 'divider'],
      ];
      const caretAt = el.selectionStart;
      const head = value.slice(0, caretAt);
      for (const [re, type, extra] of rules) {
        if (b.type !== 'p' && type !== 'todo') continue;
        if (re.test(head) && caretAt === head.length) {
          const rest = value.slice(caretAt);
          if (type === 'divider') {
            patch(b.id, { text: rest });
            turnInto(b.id, 'divider');
            return;
          }
          set(current.current.map((x) => (x.id === b.id ? { id: x.id, type, text: rest, ...(type === 'todo' ? { checked: false } : {}), ...(extra ?? {}) } : x)));
          focus(b.id, 0);
          return;
        }
      }
    }
    if (b.type !== 'code' && b.type !== 'image' && /^\/[^\s/]{0,20}$/.test(value)) setSlash({ id: b.id, query: value.slice(1), index: 0 });
    else if (slash?.id === b.id) setSlash(null);
    patch(b.id, { text: value });
  };

  const onKey = (b: Block, e: KeyboardEvent<HTMLTextAreaElement>) => {
    const el = e.currentTarget;
    const at = el.selectionStart;
    const atEnd = el.selectionEnd;
    const i = index(b.id);
    const mod = e.metaKey || e.ctrlKey;
    if (slash && slash.id === b.id) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const n = items.length || 1;
        setSlash({ ...slash, index: (slash.index + (e.key === 'ArrowDown' ? 1 : n - 1)) % n });
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        const it = items[slash.index];
        if (it) void applySlash(it);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setSlash(null);
        return;
      }
    }
    if (mod && !e.altKey) {
      const k = e.key.toLowerCase();
      const wrap = (o: string) => {
        e.preventDefault();
        const r = toggleMark(el.value, at, atEnd, o);
        patch(b.id, { text: r.value });
        focus(b.id, r.start, r.end);
      };
      if (k === 'b') return wrap('**');
      if (k === 'i') return wrap('*');
      if (k === 'e') return wrap('`');
      if (k === 'x' && e.shiftKey) return wrap('~~');
      if (k === 'k') {
        e.preventDefault();
        setLinking({ id: b.id, start: at, end: atEnd });
        return;
      }
      if (e.key === 'Enter' && b.type === 'todo') {
        e.preventDefault();
        patch(b.id, { checked: !b.checked });
        return;
      }
    }
    if (e.key === 'Enter' && !e.shiftKey && !(b.type === 'code' && !mod)) {
      e.preventDefault();
      if (LISTS.includes(b.type) && !b.text) {
        if (b.indent) patch(b.id, { indent: b.indent - 1 || undefined });
        else turnInto(b.id, 'p');
        return;
      }
      if (b.type === 'code') {
        const nb = blank();
        insertAfter(b.id, [nb]);
        focus(nb.id, 0);
        return;
      }
      const before = b.text.slice(0, at);
      const after = b.text.slice(atEnd);
      // At the very start of a heading: an empty line above it, the heading stays where the cursor is.
      if (at === 0 && b.text && !LISTS.includes(b.type)) {
        const nb = blank();
        const list = [...current.current];
        list.splice(i, 0, nb);
        set(list);
        focus(b.id, 0);
        return;
      }
      const keep = LISTS.includes(b.type) ? b.type : b.type === 'quote' && after ? 'quote' : 'p';
      const nb = blank(keep, { text: after, ...(keep === 'todo' ? { checked: false } : {}), ...(LISTS.includes(keep) && b.indent ? { indent: b.indent } : {}) });
      set(current.current.flatMap((x) => (x.id === b.id ? [{ ...x, text: before }, nb] : [x])));
      focus(nb.id, 0);
      return;
    }
    if (e.key === 'Backspace' && at === 0 && atEnd === 0) {
      if (b.type !== 'p') {
        e.preventDefault();
        if (LISTS.includes(b.type) && b.indent) patch(b.id, { indent: b.indent - 1 || undefined });
        else turnInto(b.id, 'p');
        return;
      }
      const prev = current.current[i - 1];
      if (!prev) {
        if (!b.text && current.current.length > 1) {
          e.preventDefault();
          remove(b.id);
        } else if (!b.text) {
          e.preventDefault();
          onTitleFocus();
        }
        return;
      }
      e.preventDefault();
      if (!editable(prev) || prev.type === 'image') {
        if (!b.text) remove(b.id);
        else set(current.current.filter((x) => x.id !== prev.id));
        return;
      }
      const join = prev.text.length;
      set(current.current.filter((x) => x.id !== b.id).map((x) => (x.id === prev.id ? { ...x, text: x.text + b.text } : x)));
      focus(prev.id, join);
      return;
    }
    if (e.key === 'Delete' && at === b.text.length && atEnd === at) {
      const next = current.current[i + 1];
      if (next && editable(next) && next.type !== 'image') {
        e.preventDefault();
        set(current.current.filter((x) => x.id !== next.id).map((x) => (x.id === b.id ? { ...x, text: x.text + next.text } : x)));
        focus(b.id, at);
      }
      return;
    }
    if (e.key === 'Tab' && LISTS.includes(b.type)) {
      e.preventDefault();
      const prev = current.current[i - 1];
      const max = prev && LISTS.includes(prev.type) ? Math.min(4, (prev.indent ?? 0) + 1) : 0;
      const n = e.shiftKey ? Math.max(0, (b.indent ?? 0) - 1) : Math.min(max, (b.indent ?? 0) + 1);
      patch(b.id, { indent: n || undefined });
      focus(b.id, at, atEnd);
      return;
    }
    if (e.key === 'ArrowUp' && !e.shiftKey && !el.value.slice(0, at).includes('\n')) {
      const prev = prevEditable(i);
      e.preventDefault();
      if (prev) focus(prev.id);
      else onTitleFocus();
      return;
    }
    if (e.key === 'ArrowDown' && !e.shiftKey && !el.value.slice(atEnd).includes('\n')) {
      const next = nextEditable(i);
      if (next) {
        e.preventDefault();
        focus(next.id, 0);
      }
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      el.blur();
    }
  };

  const onPaste = (b: Block, e: ClipboardEvent<HTMLTextAreaElement>) => {
    const pics = [...e.clipboardData.files].filter((f) => f.type.startsWith('image/'));
    if (pics.length) {
      e.preventDefault();
      void addPictures(pics, b.id);
      return;
    }
    const text = e.clipboardData.getData('text/plain');
    if (!text.includes('\n') || b.type === 'code') return;
    e.preventDefault();
    const el = e.currentTarget;
    const before = b.text.slice(0, el.selectionStart);
    const after = b.text.slice(el.selectionEnd);
    const parsed = fromLines(text.replace(/\r\n?/g, '\n').split('\n'));
    if (!parsed.length) return;
    const first = parsed[0]!;
    const rest = parsed.slice(1);
    const head: Block = first.type === 'p' || !before ? { ...b, ...(before ? {} : { type: first.type, checked: first.checked, indent: first.indent }), text: before + first.text } : b;
    const tail = rest.length ? rest : [];
    const last = tail[tail.length - 1];
    if (last) last.text += after;
    else head.text += after;
    const list = current.current.flatMap((x) => (x.id === b.id ? [head, ...(first.type === 'p' || !before ? [] : [first]), ...tail] : [x]));
    set(list);
    const end = last ?? head;
    focus(end.id, end.text.length - after.length);
  };

  // ---- dragging blocks and dropping pictures --------------------------------------------------
  const onDragOverBlock = (b: Block, e: DragEvent) => {
    if (readOnly) return;
    const files = e.dataTransfer.types.includes('Files');
    if (!drag && !files) return;
    e.preventDefault();
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const after = e.clientY > r.top + r.height / 2;
    setDrag((d) => ({ id: d?.id ?? '', over: b.id, after }));
  };
  const onDrop = (e: DragEvent) => {
    if (readOnly) return;
    const d = drag;
    setDrag(null);
    if (e.dataTransfer.files.length) {
      e.preventDefault();
      const target = d?.over ? (d.after ? d.over : (current.current[index(d.over) - 1]?.id ?? null)) : null;
      void addPictures([...e.dataTransfer.files], target ?? current.current[current.current.length - 1]?.id ?? null);
      return;
    }
    if (d?.id && d.over) {
      e.preventDefault();
      moveTo(d.id, d.over, d.after);
    }
  };

  // ---- numbering of numbered lists ------------------------------------------------------------
  const numbers = new Map<string, number>();
  {
    const count: number[] = [];
    for (const b of blocks) {
      if (b.type !== 'number') {
        if (!LISTS.includes(b.type)) count.length = 0;
        continue;
      }
      const lvl = b.indent ?? 0;
      count.length = lvl + 1;
      count[lvl] = (count[lvl] ?? 0) + 1;
      numbers.set(b.id, count[lvl]!);
    }
  }

  const placeholder = (b: Block, i: number) =>
    b.type === 'h1' || b.type === 'h2' || b.type === 'h3'
      ? t(TYPE_LABEL[b.type])
      : b.type === 'p'
        ? active === b.id
          ? t('kb.ph_write')
          : i === 0 && blocks.length === 1
            ? t('kb.ph_empty')
            : ''
        : b.type === 'todo'
          ? t('kb.ph_todo')
          : b.type === 'image'
            ? t('kb.ph_caption')
            : LISTS.includes(b.type)
              ? t('kb.ph_list')
              : '';

  const textOf = (b: Block, i: number, cls: string) => {
    if (!readOnly && active === b.id)
      return (
        <AutoText
          className={`kb-input ${cls}`}
          value={b.text}
          placeholder={placeholder(b, i)}
          aria-label={t(TYPE_LABEL[b.type])}
          onRef={(el) => (el ? areas.current.set(b.id, el) : areas.current.delete(b.id))}
          onChange={(e) => onText(b, e.target.value, e.target)}
          onKeyDown={(e) => onKey(b, e)}
          onPaste={(e) => onPaste(b, e)}
          onSelect={(e) => setSelBar(e.currentTarget.selectionStart !== e.currentTarget.selectionEnd && b.type !== 'code' ? b.id : null)}
          onBlur={() => {
            // Leaving the page's text altogether, not just going to the next block.
            setTimeout(() => {
              if (!document.activeElement?.closest('.kb-editor .kb-input, .kb-selbar, .kb-slash')) {
                setActive((x) => (x === b.id ? null : x));
                setSlash(null);
                setSelBar(null);
              }
            }, 0);
          }}
        />
      );
    const empty = !b.text;
    return (
      <div
        className={`kb-text ${cls}${empty ? ' kb-empty' : ''}`}
        data-placeholder={empty && !readOnly ? placeholder(b, i) : undefined}
        tabIndex={readOnly ? undefined : 0}
        onFocus={(e) => {
          if (readOnly || e.target !== e.currentTarget) return;
          focus(b.id);
        }}
        onMouseUp={(e) => {
          if (readOnly || (e.target as HTMLElement).closest('a')) return;
          if (window.getSelection()?.toString()) return;
          const at = storedOffset(e.currentTarget);
          focus(b.id, at ?? 'end');
        }}
      >
        {b.type === 'code' ? b.text : <Inline text={b.text} />}
      </div>
    );
  };

  const body = (b: Block, i: number) => {
    switch (b.type) {
      case 'divider':
        return (
          <div
            className="kb-divider"
            tabIndex={readOnly ? undefined : 0}
            role="separator"
            onKeyDown={(e) => {
              if (e.key === 'Backspace' || e.key === 'Delete') {
                e.preventDefault();
                remove(b.id);
              } else if (e.key === 'Enter') {
                e.preventDefault();
                const nb = blank();
                insertAfter(b.id, [nb]);
                focus(nb.id, 0);
              } else if (e.key === 'ArrowUp') {
                const p = prevEditable(i);
                if (p) focus(p.id);
              } else if (e.key === 'ArrowDown') {
                const n = nextEditable(i);
                if (n) focus(n.id, 0);
              }
            }}
          >
            <hr />
          </div>
        );
      case 'image':
        return (
          <figure className="kb-figure">
            {b.image ? <Picture id={b.image} alt={plain(b.text)} /> : null}
            <figcaption>{readOnly && !b.text ? null : textOf(b, i, 'kb-caption')}</figcaption>
          </figure>
        );
      case 'todo':
        return (
          <>
            <input
              type="checkbox"
              className="kb-check"
              checked={!!b.checked}
              disabled={readOnly}
              onChange={(e) => patch(b.id, { checked: e.target.checked })}
              aria-label={b.checked ? t('kb.done') : t('kb.not_done')}
            />
            {textOf(b, i, b.checked ? 'kb-done' : '')}
          </>
        );
      case 'bullet':
        return (
          <>
            <span className="kb-marker" aria-hidden="true">
              {['•', '◦', '▪'][(b.indent ?? 0) % 3]}
            </span>
            {textOf(b, i, '')}
          </>
        );
      case 'number':
        return (
          <>
            <span className="kb-marker kb-num" aria-hidden="true">
              {numbers.get(b.id)}.
            </span>
            {textOf(b, i, '')}
          </>
        );
      case 'callout':
        return (
          <>
            <Icon name={TONE_ICON[b.tone ?? 'info']} size={18} className="kb-callout-icon" />
            {textOf(b, i, '')}
          </>
        );
      default:
        return textOf(b, i, '');
    }
  };

  const tag = (b: Block) => (b.type === 'h1' ? 'h2' : b.type === 'h2' ? 'h3' : b.type === 'h3' ? 'h4' : 'div');

  return (
    <div
      className={`kb-editor${readOnly ? ' ro' : ''}`}
      onDragOver={(e) => {
        if (!readOnly && e.dataTransfer.types.includes('Files')) e.preventDefault();
      }}
      onDrop={onDrop}
      onDragEnd={() => setDrag(null)}
    >
      {blocks.map((b, i) => {
        const Tag = tag(b);
        const showDrop = drag?.over === b.id && (drag.id !== b.id || !drag.id);
        return (
          <Tag
            key={b.id}
            data-block={b.id}
            className={`kb-b kb-${b.type}${b.indent ? ` kb-in${b.indent}` : ''}${b.type === 'callout' ? ` tone-${b.tone ?? 'info'}` : ''}${active === b.id ? ' active' : ''}${showDrop ? (drag!.after ? ' drop-after' : ' drop-before') : ''}`}
            onDragOver={(e: DragEvent) => onDragOverBlock(b, e)}
          >
            {readOnly ? null : (
              <button
                type="button"
                className="kb-grip"
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.effectAllowed = 'move';
                  e.dataTransfer.setData('text/plain', b.id);
                  setDrag({ id: b.id, over: null, after: false });
                  setMenu(null);
                }}
                onClick={() => setMenu(menu === b.id ? null : b.id)}
                aria-label={t('kb.block_options')}
                aria-expanded={menu === b.id}
                title={t('kb.block_options_hint')}
              >
                <Icon name="grip" size={16} />
              </button>
            )}
            {body(b, i)}
            {menu === b.id ? (
              <BlockMenu
                b={b}
                first={i === 0}
                last={i === blocks.length - 1}
                onClose={() => setMenu(null)}
                onType={(type) => {
                  setMenu(null);
                  turnInto(b.id, type);
                }}
                onTone={(tone) => {
                  setMenu(null);
                  patch(b.id, { tone });
                }}
                onMove={(d) => {
                  setMenu(null);
                  moveBy(b.id, d);
                }}
                onCopy={() => {
                  setMenu(null);
                  insertAfter(b.id, [{ ...b, id: newId() }]);
                }}
                onDelete={() => {
                  setMenu(null);
                  remove(b.id);
                }}
              />
            ) : null}
            {slash?.id === b.id && items.length ? (
              <div className="kb-slash" role="listbox" aria-label={t('kb.slash_title')}>
                {items.map((it, k) => (
                  <button
                    key={it.key}
                    type="button"
                    role="option"
                    aria-selected={k === slash.index}
                    className={k === slash.index ? 'on' : ''}
                    onMouseDown={(e) => e.preventDefault()}
                    onMouseEnter={() => setSlash({ ...slash, index: k })}
                    onClick={() => void applySlash(it)}
                  >
                    <span className="kb-slash-icon">
                      <Icon name={it.icon} size={16} />
                    </span>
                    <span>{it.label}</span>
                    {it.hint ? <kbd>{it.hint}</kbd> : null}
                  </button>
                ))}
              </div>
            ) : null}
            {selBar === b.id && active === b.id ? (
              <div className="kb-selbar" role="toolbar" aria-label={t('kb.format')}>
                {(
                  [
                    ['**', 'kb.bold', 'B'],
                    ['*', 'kb.italic', 'I'],
                    ['~~', 'kb.strike', 'S'],
                    ['`', 'kb.code', '</>'],
                  ] as const
                ).map(([m, label, glyph]) => (
                  <button
                    key={m}
                    type="button"
                    className={`kb-fmt kb-fmt-${label.slice(3)}`}
                    aria-label={t(label)}
                    title={t(label)}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      const el = areas.current.get(b.id);
                      if (!el) return;
                      const r = toggleMark(el.value, el.selectionStart, el.selectionEnd, m);
                      patch(b.id, { text: r.value });
                      focus(b.id, r.start, r.end);
                    }}
                  >
                    {glyph}
                  </button>
                ))}
                <button
                  type="button"
                  className="kb-fmt"
                  aria-label={t('kb.link')}
                  title={t('kb.link')}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    const el = areas.current.get(b.id);
                    if (el) setLinking({ id: b.id, start: el.selectionStart, end: el.selectionEnd });
                  }}
                >
                  <Icon name="link" size={16} />
                </button>
              </div>
            ) : null}
          </Tag>
        );
      })}
      {!readOnly ? (
        <button
          type="button"
          className="kb-tail"
          aria-label={t('kb.add_block')}
          onClick={() => {
            const last = current.current[current.current.length - 1];
            if (last && last.type === 'p' && !last.text) return focus(last.id);
            const nb = blank();
            set([...current.current, nb]);
            focus(nb.id, 0);
          }}
        >
          {blocks.length ? null : t('kb.ph_empty')}
        </button>
      ) : null}
      {uploading ? (
        <p className="kb-uploading muted small" role="status">
          <span className="spinner" aria-hidden="true" /> {t('kb.uploading')}
        </p>
      ) : null}
      <input
        ref={file}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={(e) => {
          const files = [...(e.target.files ?? [])];
          e.target.value = '';
          const { after, replace } = fileFor.current;
          void addPictures(files, after ?? replace, replace);
        }}
      />
      {linking ? (
        <LinkDialog
          pages={pages}
          selected={current.current.find((b) => b.id === linking.id)?.text.slice(linking.start, linking.end) ?? ''}
          onClose={() => {
            const id = linking.id;
            setLinking(null);
            focus(id, linking.end);
          }}
          onPick={(label, href) => {
            const b = current.current.find((x) => x.id === linking.id);
            setLinking(null);
            if (!b) return;
            const md = `[${label}](${href})`;
            const text = linking.replace ? md : b.text.slice(0, linking.start) + md + b.text.slice(linking.end);
            patch(b.id, { text });
            focus(b.id, (linking.replace ? 0 : linking.start) + md.length);
          }}
        />
      ) : null}
    </div>
  );
});

function BlockMenu({
  b,
  first,
  last,
  onClose,
  onType,
  onTone,
  onMove,
  onCopy,
  onDelete,
}: {
  b: Block;
  first: boolean;
  last: boolean;
  onClose: () => void;
  onType: (t: BlockType) => void;
  onTone: (t: CalloutTone) => void;
  onMove: (d: number) => void;
  onCopy: () => void;
  onDelete: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.querySelector<HTMLElement>('button')?.focus();
    const away = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && !(e.target as HTMLElement).closest('.kb-grip') && onClose();
    const esc = (e: globalThis.KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', esc);
    };
  }, [onClose]);
  const types = BLOCK_TYPES.filter((x) => x !== b.type && x !== 'image' && (b.type !== 'image' || x === 'p'));
  return (
    <div className="kb-menu" role="menu" ref={ref} aria-label={t('kb.block_options')}>
      <p className="kb-menu-head">{t('kb.turn_into')}</p>
      <div className="kb-menu-types">
        {types.map((x) => (
          <button key={x} type="button" role="menuitem" onClick={() => onType(x)}>
            <Icon name={TYPE_ICON[x]} size={16} />
            {t(TYPE_LABEL[x])}
          </button>
        ))}
      </div>
      {b.type === 'callout' ? (
        <>
          <p className="kb-menu-head">{t('kb.colour')}</p>
          <div className="kb-menu-tones">
            {CALLOUT_TONES.map((x) => (
              <button key={x} type="button" role="menuitemradio" aria-checked={(b.tone ?? 'info') === x} className={`kb-tone tone-${x}`} onClick={() => onTone(x)}>
                <Icon name={TONE_ICON[x]} size={14} />
                {t(`kb.tone_${x}` as MessageKey)}
              </button>
            ))}
          </div>
        </>
      ) : null}
      <hr />
      <button type="button" role="menuitem" onClick={() => onMove(-1)} disabled={first}>
        <Icon name="arrowUp" size={16} />
        {t('kb.move_up')}
      </button>
      <button type="button" role="menuitem" onClick={() => onMove(1)} disabled={last}>
        <Icon name="arrowDown" size={16} />
        {t('kb.move_down')}
      </button>
      <button type="button" role="menuitem" onClick={onCopy}>
        <Icon name="copy" size={16} />
        {t('kb.duplicate_block')}
      </button>
      <button type="button" role="menuitem" className="danger" onClick={onDelete}>
        <Icon name="trash" size={16} />
        {t('kb.delete_block')}
      </button>
    </div>
  );
}

/** A link to a web address, or to another page of the handbook. */
function LinkDialog({ pages, selected, onClose, onPick }: { pages: PageRef[]; selected: string; onClose: () => void; onPick: (label: string, href: string) => void }) {
  const [q, setQ] = useState('');
  const [label, setLabel] = useState(selected);
  const url = q.trim();
  const isUrl = /^(https?:\/\/|mailto:|www\.)\S+$/i.test(url) || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(url);
  const href = isUrl ? (/^www\./i.test(url) ? `https://${url}` : url.includes('@') && !/^mailto:/i.test(url) && !/^https?:/i.test(url) ? `mailto:${url}` : url) : '';
  const found = pages.filter((p) => !url || (p.title || t('kb.untitled')).toLowerCase().includes(url.toLowerCase())).slice(0, 8);
  return (
    <Dialog
      title={t('kb.link_title')}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" disabled={!href} onClick={() => onPick(label.trim() || url, href)}>
            {t('kb.link_add')}
          </Button>
        </>
      }
    >
      <div className="kb-link">
        <label className="field">
          <span className="field-label">{t('kb.link_to')}</span>
          <input
            className="input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('kb.link_ph')}
            data-autofocus
            onKeyDown={(e) => e.key === 'Enter' && href && onPick(label.trim() || url, href)}
          />
        </label>
        <label className="field">
          <span className="field-label">{t('kb.link_text')}</span>
          <input className="input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder={t('kb.link_text_ph')} />
        </label>
        {!isUrl ? (
          <div className="kb-link-pages">
            <p className="muted small">{t('kb.link_pages')}</p>
            {found.length ? (
              found.map((p) => (
                <button key={p.id} type="button" className="kb-link-page" onClick={() => onPick(label.trim() || p.title || t('kb.untitled'), `/knowledge/${p.id}`)}>
                  <span className="kb-emoji" aria-hidden="true">
                    {p.icon || <Icon name="text" size={16} />}
                  </span>
                  {p.title || t('kb.untitled')}
                </button>
              ))
            ) : (
              <p className="muted small">{t('kb.no_pages_found')}</p>
            )}
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}
